import {
  MoveOutStepDate,
  MoveOutTimeline,
  MoveOutTimelineStep,
} from "@/api/model"
import {
  DateFormats,
  formatDate,
  isSameMonth,
  isSameYear,
  parseApiDate,
} from "@/lib/dateHelper"
import { Dayjs } from "dayjs"
import {
  StepItem,
  StepperItemState,
  StepperItemStateType,
} from "@/components/ui/stepper/StepperTypes"

export const MoveOutTimelineStatus = {
  NOT_MOVING_OUT: "NOT_MOVING_OUT",
  MOVE_OUT_PENDING: "MOVE_OUT_PENDING",
  MOVED_OUT: "MOVED_OUT",
  CLOSED: "CLOSED",
  ERROR: "ERROR",
} as const

export const MoveOutStepKey = {
  MOVE_OUT_INITIATED: "MOVE_OUT_INITIATED",
  METER_READING: "METER_READING",
  FINAL_BILL: "FINAL_BILL",
  PAYMENT_SETTLED: "PAYMENT_SETTLED",
  CLOSURE_COMPLETE: "CLOSURE_COMPLETE",
} as const

export const MoveOutStepStatus = {
  UNAVAILABLE: "UNAVAILABLE",
} as const

export const MoveOutInitiatedStatus = {
  NOT_INITIATED: "NOT_INITIATED",
  INITIATED: "INITIATED",
} as const

export const MoveOutStepDateType = {
  ACTION: "ACTION",
  THRESHOLD: "THRESHOLD",
} as const

export const MoveOutMeterReadingStatus = {
  PENDING: "PENDING",
  EXPECTED_ELECTRICITY_READ: "EXPECTED_ELECTRICITY_READ",
  EXPECTED_GAS_READ: "EXPECTED_GAS_READ",
  EXPECTED_MANUAL_READ: "EXPECTED_MANUAL_READ",
  READ: "READ",
  ESTIMATED: "ESTIMATED",
  ESTIMATED_ELECTRICITY_READ: "ESTIMATED_ELECTRICITY_READ",
  ESTIMATED_GAS_READ: "ESTIMATED_GAS_READ",
  ACTION_NEEDED: "ACTION_NEEDED",
} as const

export const MoveOutFinalBillStatus = {
  PENDING: "PENDING",
  EXPECTED: "EXPECTED",
  EXPECTED_WITHOUT_READING: "EXPECTED_WITHOUT_READING",
  ISSUED: "ISSUED",
} as const

export const MoveOutPaymentSettledStatus = {
  PENDING: "PENDING",
  PAYMENT_DUE: "PAYMENT_DUE",
  REFUND_DUE: "REFUND_DUE",
  SETTLED: "SETTLED",
} as const

export const MoveOutClosureStatus = {
  CLOSED: "CLOSED",
} as const

const visibleStatuses = new Set<string>([
  MoveOutTimelineStatus.MOVE_OUT_PENDING,
  MoveOutTimelineStatus.MOVED_OUT,
  MoveOutTimelineStatus.CLOSED,
])

const stepTitles: Record<string, string> = {
  [MoveOutStepKey.MOVE_OUT_INITIATED]: "Move out initiated",
  [MoveOutStepKey.METER_READING]: "Meter reading confirmed",
  [MoveOutStepKey.FINAL_BILL]: "Final bill",
  [MoveOutStepKey.PAYMENT_SETTLED]: "Payment settled",
  [MoveOutStepKey.CLOSURE_COMPLETE]: "Closure complete",
}

// The timeline appears from the LDOR, compared on the UK calendar date.
// Without an LDOR we can't tell, so it stays hidden.
const hasReachedLdor = (timeline: MoveOutTimeline, now: Dayjs) => {
  const ldor = parseApiDate(timeline.lastDateOfResponsibility)
  if (!ldor) return false

  return now.format(DateFormats.api) >= ldor.format(DateFormats.api)
}

export const shouldShowMoveOutTimeline = (
  isFlagEnabled: boolean,
  timeline: MoveOutTimeline | undefined,
  now: Dayjs,
) =>
  isFlagEnabled &&
  !!timeline &&
  visibleStatuses.has(timeline.status) &&
  hasReachedLdor(timeline, now)

// Booked whatever the LDOR, unlike the timeline card.
export const isMoveOutBooked = (
  isFlagEnabled: boolean,
  timeline?: MoveOutTimeline,
) => isFlagEnabled && !!timeline && visibleStatuses.has(timeline.status)

export const hasUnavailableStep = (timeline?: MoveOutTimeline) =>
  !!timeline?.steps.some(
    ({ status }) => status === MoveOutStepStatus.UNAVAILABLE,
  )

const formatStepDate = (actionDate?: MoveOutStepDate) => {
  if (!actionDate) return ""

  const from = formatDate(actionDate.from, "long")
  if (!actionDate.to) return from

  return `${from} – ${formatDate(actionDate.to, "long")}`
}

// Each step has its own statuses, so each maps them to a stepper state.
// Unknown statuses show as pending.
const stepStates: Record<string, Record<string, StepperItemStateType>> = {
  [MoveOutStepKey.MOVE_OUT_INITIATED]: {
    [MoveOutInitiatedStatus.INITIATED]: StepperItemState.COMPLETE,
  },
  [MoveOutStepKey.METER_READING]: {
    [MoveOutMeterReadingStatus.READ]: StepperItemState.COMPLETE,
    [MoveOutMeterReadingStatus.ESTIMATED]: StepperItemState.COMPLETE,
    [MoveOutMeterReadingStatus.ESTIMATED_ELECTRICITY_READ]:
      StepperItemState.COMPLETE,
    [MoveOutMeterReadingStatus.ESTIMATED_GAS_READ]: StepperItemState.COMPLETE,
  },
  [MoveOutStepKey.FINAL_BILL]: {
    [MoveOutFinalBillStatus.ISSUED]: StepperItemState.COMPLETE,
  },
  [MoveOutStepKey.PAYMENT_SETTLED]: {
    [MoveOutPaymentSettledStatus.SETTLED]: StepperItemState.COMPLETE,
  },
  [MoveOutStepKey.CLOSURE_COMPLETE]: {
    [MoveOutClosureStatus.CLOSED]: StepperItemState.COMPLETE,
  },
}

const getStepState = ({ key, status }: MoveOutTimelineStep) =>
  stepStates[key]?.[status] ?? StepperItemState.IDLE

const isStepComplete = (timeline: MoveOutTimeline | undefined, key: string) => {
  const step = timeline?.steps.find((step) => step.key === key)
  return !!step && getStepState(step) === StepperItemState.COMPLETE
}

export const MoveOutSupportingText = {
  CANCEL_MOVE: "CANCEL_MOVE",
  READINGS_CONFIRMED: "READINGS_CONFIRMED",
} as const

export type MoveOutSupportingTextType =
  (typeof MoveOutSupportingText)[keyof typeof MoveOutSupportingText]

// The message under the stepper depends on the furthest step reached.
// Nothing shows once the final bill is issued.
export const getMoveOutSupportingText = (
  timeline?: MoveOutTimeline,
): MoveOutSupportingTextType | undefined => {
  const isComplete = (key: string) => isStepComplete(timeline, key)

  if (isComplete(MoveOutStepKey.FINAL_BILL)) return undefined
  if (isComplete(MoveOutStepKey.METER_READING)) {
    return MoveOutSupportingText.READINGS_CONFIRMED
  }
  if (isComplete(MoveOutStepKey.MOVE_OUT_INITIATED)) {
    return MoveOutSupportingText.CANCEL_MOVE
  }

  return undefined
}

const EXPECTED_ABBREVIATION = "Exp."
const EXPECTED_LABEL = "Expected"

const formatCompactDateRange = ({ from, to }: MoveOutStepDate): string => {
  if (!to) return formatDate(from, "long")

  if (!isSameYear(from, to)) {
    return `${formatDate(from, "long")} – ${formatDate(to, "long")}`
  }

  if (isSameMonth(from, to)) {
    return `${formatDate(from, "dayOnly")}–${formatDate(to, "long")}`
  }

  return `${formatDate(from, "dayMonthShort")} – ${formatDate(to, "long")}`
}

type StepDescription = Pick<
  StepItem,
  "description" | "descriptionAriaLabel" | "note" | "badge"
>

const findStepDate = (step: MoveOutTimelineStep, type: string) =>
  step.dates.find((date) => date.type === type)

// The expected range only shows once the meter reading step is complete: the BE
// also dates a bill expected without a reading, while step 2 needs action.
const formatFinalBillDescription = (
  step: MoveOutTimelineStep,
  state: StepperItemStateType,
  timeline?: MoveOutTimeline,
): StepDescription => {
  if (!step.actionDate) return { description: "" }

  if (state === StepperItemState.COMPLETE) {
    return { description: formatDate(step.actionDate.from, "long") }
  }

  if (!isStepComplete(timeline, MoveOutStepKey.METER_READING)) {
    return { description: "" }
  }

  const range = formatCompactDateRange(step.actionDate)

  return {
    description: `${EXPECTED_ABBREVIATION} ${range}`,
    descriptionAriaLabel: `${EXPECTED_LABEL} ${range}`,
  }
}

// The date only shows once the balance is settled: while payment is due, the
// BE sends the bill's due date, which the timeline doesn't show.
const formatPaymentSettledDescription = (
  step: MoveOutTimelineStep,
  state: StepperItemStateType,
): StepDescription => ({
  description:
    state === StepperItemState.COMPLETE ? formatStepDate(step.actionDate) : "",
})

// The fuel named in the Estimated tooltip when only one fuel was estimated.
const estimatedFuels: Record<string, string> = {
  [MoveOutMeterReadingStatus.ESTIMATED_ELECTRICITY_READ]: "electricity",
  [MoveOutMeterReadingStatus.ESTIMATED_GAS_READ]: "gas",
}

// The reading window isn't shown while it's open: the step stays grey with no
// date until the reading is in, estimated or needs the customer to call us.
const formatMeterReadingDescription = (
  step: MoveOutTimelineStep,
): StepDescription => {
  const actionDate = findStepDate(step, MoveOutStepDateType.ACTION)

  switch (step.status) {
    case MoveOutMeterReadingStatus.READ:
      return { description: formatStepDate(actionDate) }
    case MoveOutMeterReadingStatus.ESTIMATED:
    case MoveOutMeterReadingStatus.ESTIMATED_ELECTRICITY_READ:
    case MoveOutMeterReadingStatus.ESTIMATED_GAS_READ: {
      const deadline = findStepDate(step, MoveOutStepDateType.THRESHOLD)
      const byDeadline = deadline ? ` by ${formatStepDate(deadline)}` : ""
      const fuel = estimatedFuels[step.status]
      const manualReading = fuel ? `manual ${fuel} reading` : "manual reading"

      return {
        description: formatStepDate(actionDate),
        note: {
          label: "Estimated",
          tooltip: `We didn't receive a ${manualReading}${byDeadline}, so we've used an estimate for your final bill.`,
        },
      }
    }
    case MoveOutMeterReadingStatus.ACTION_NEEDED:
      return { description: "", badge: "Action needed" }
    default:
      return { description: "" }
  }
}

// Mirrors stepStates: steps without an entry fall back to formatStepDate.
const stepDescriptions: Record<string, typeof formatFinalBillDescription> = {
  [MoveOutStepKey.METER_READING]: formatMeterReadingDescription,
  [MoveOutStepKey.FINAL_BILL]: formatFinalBillDescription,
  [MoveOutStepKey.PAYMENT_SETTLED]: formatPaymentSettledDescription,
}

const getStepDescription = (
  step: MoveOutTimelineStep,
  state: StepperItemStateType,
  timeline?: MoveOutTimeline,
): StepDescription =>
  stepDescriptions[step.key]?.(step, state, timeline) ?? {
    description: formatStepDate(step.actionDate),
  }

export const getMoveOutTimelineSteps = (
  timeline?: MoveOutTimeline,
): StepItem[] =>
  (timeline?.steps ?? [])
    .filter(({ key }) => key in stepTitles)
    .map((step) => {
      const state = getStepState(step)

      return {
        title: stepTitles[step.key],
        ...getStepDescription(step, state, timeline),
        state,
        isCompleted: state === StepperItemState.COMPLETE,
      }
    })
